import React, { useState, useEffect, useCallback } from 'react';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { browseListings } from '../../lib/buyerApi';
import { DEFAULT_CURRENCY, formatMoney } from '../../components/marketplace/cartUtils';

const CATEGORIES = ['All', 'Fruits', 'Vegetables', 'Grains', 'Legumes', 'Spices', 'Dairy', 'Livestock'];

const BuyerMarketplacePage: React.FC = () => {
  const [listings, setListings] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [sortBy, setSortBy] = useState('created_at');
  const [page, setPage] = useState(1);
  const [addedId, setAddedId] = useState<number | null>(null);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 24, sort: sortBy, order: 'DESC' };
      if (search) params.search = search;
      if (category !== 'All') params.category = category;
      const result = await browseListings(params);
      setListings(result.items || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Failed to load listings:', err);
    } finally {
      setLoading(false);
    }
  }, [page, category, search, sortBy]);

  useEffect(() => { loadListings(); }, [loadListings]);

  const handleAddToCart = (listing: any) => {
    // Add to localStorage cart (reuse existing cart format)
    const CART_KEY = 'esanza_marketplace_cart_v3';
    try {
      const existing = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
      const minQty = Number(listing.minimum_order_quantity) || 1;
      const existingIdx = existing.findIndex((i: any) => i.listingId === listing.id);
      if (existingIdx >= 0) {
        existing[existingIdx].quantity += minQty;
        existing[existingIdx].subtotal = existing[existingIdx].quantity * existing[existingIdx].pricePerUnit;
      } else {
        existing.push({
          listingId: listing.id,
          title: listing.title,
          catalogName: listing.catalog_name,
          quantity: minQty,
          pricePerUnit: Number(listing.price_per_unit) || 0,
          unit: listing.commercial_unit,
          normalizedUnit: listing.normalized_unit || listing.commercial_unit,
          conversionFactor: Number(listing.conversion_factor) || 1,
          currency: listing.currency || DEFAULT_CURRENCY,
          subtotal: minQty * (Number(listing.price_per_unit) || 0),
          supplierName: listing.supplier_name,
          tenantId: Number(listing.tenant_id) || 0,
          minimumOrderQuantity: minQty,
          maximumOrderQuantity: listing.maximum_order_quantity || null,
          availableQuantity: listing.available_quantity || 0,
          quantityStep: listing.quantity_step || 1,
          imageUrl: listing.image_url || undefined,
        });
      }
      localStorage.setItem(CART_KEY, JSON.stringify(existing));
      setAddedId(listing.id);
      setTimeout(() => setAddedId(null), 2000);
    } catch (err) {
      console.error('Cart error:', err);
    }
  };

  return (
    <BuyerPortalLayout title="Marketplace">
      {/* Search & Filters */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <input type="text" placeholder="Search products, suppliers..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:bg-white outline-none transition-all" />
          </div>
          <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm">
            {CATEGORIES.map(c => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
          </select>
          <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm">
            <option value="created_at">Newest First</option>
            <option value="price_per_unit">Price: Low to High</option>
          </select>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">{total} listing{total !== 1 ? 's' : ''} found</p>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 animate-pulse">
              <div className="h-32 bg-gray-100 rounded-xl mb-3" />
              <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <p className="text-5xl mb-4">🌿</p>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No listings found</h3>
          <p className="text-gray-500 text-sm">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {listings.map(listing => (
            <div key={listing.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-lg transition-all group">
              <div className="h-32 bg-gradient-to-br from-emerald-50 to-emerald-100 relative">
                {listing.image_url ? (
                  <img src={listing.image_url} alt={listing.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><span className="text-4xl">🌿</span></div>
                )}
                {listing.supplier_verified === 'verified' && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-md">Verified</span>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-sm font-bold text-gray-900 truncate">{listing.title}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{listing.supplier_name || 'Verified Supplier'}</p>
                {listing.grade && <span className="mt-2 inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">Grade {listing.grade}</span>}
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-lg font-extrabold text-emerald-600">{formatMoney(Number(listing.price_per_unit) || 0, listing.currency || DEFAULT_CURRENCY)}</p>
                    <p className="text-[10px] text-gray-400">per {listing.commercial_unit}</p>
                  </div>
                  <p className="text-xs text-gray-500">Min {listing.minimum_order_quantity || 1} {listing.commercial_unit}</p>
                </div>
                <button onClick={() => handleAddToCart(listing)}
                  className={`mt-3 w-full py-2 text-xs font-bold rounded-xl transition-all ${
                    addedId === listing.id
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}>
                  {addedId === listing.id ? '✓ Added' : 'Add to Cart'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {total > 24 && (
        <div className="flex justify-center gap-2 mt-6">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl disabled:opacity-40 bg-white">Previous</button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {Math.ceil(total / 24)}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 24)}
            className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl disabled:opacity-40 bg-white">Next</button>
        </div>
      )}
    </BuyerPortalLayout>
  );
};

export default BuyerMarketplacePage;
